import { useState, type FormEvent } from 'react';
import { MapPin, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import locationService, { type CreateLocationData } from '../../../../../services/locationService';
import LocationMiniMap from '../components/LocationMiniMap';
import SovereignModal from '../components/SovereignModal';
import { FieldLabel, HelperText, PRIMARY_BTN, svInput, svSecondaryBtn } from '../ui';

export type CreatedLocation = {
  _id: string;
  name: string;
  code?: string;
  state?: string;
  city?: string;
  lat: number;
  lng: number;
};

function parseCoord(raw: string, min: number, max: number): number | null {
  const s = raw.trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < min || n > max) return null;
  return n;
}

export default function AddLocationModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (loc: CreatedLocation) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [lName, setLName] = useState('');
  const [lCode, setLCode] = useState('');
  const [lType, setLType] = useState<CreateLocationData['type']>('Project Site');
  const [lState, setLState] = useState('');
  const [lCity, setLCity] = useState('');
  const [lLat, setLLat] = useState('');
  const [lLng, setLLng] = useState('');

  const lat = parseCoord(lLat, -90, 90);
  const lng = parseCoord(lLng, -180, 180);
  const latWarn = lLat.trim() !== '' && lat === null ? 'Latitude must be between −90 and 90' : undefined;
  const lngWarn = lLng.trim() !== '' && lng === null ? 'Longitude must be between −180 and 180' : undefined;

  const reset = () => {
    setLName(''); setLCode(''); setLType('Project Site');
    setLState(''); setLCity(''); setLLat(''); setLLng('');
  };
  const handleClose = () => { reset(); onClose(); };

  const handlePick = (pLat: number, pLng: number) => {
    setLLat(String(pLat));
    setLLng(String(pLng));
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (lat === null || lng === null) {
      toast.error('Latitude and longitude are required — enter them or click the map to drop a pin');
      return;
    }
    setSaving(true);
    try {
      const res = await locationService.createLocation({
        name: lName.trim(),
        code: lCode.trim().toUpperCase(),
        type: lType,
        state: lState.trim() || undefined,
        city: lCity.trim() || undefined,
        latitude: String(lat),
        longitude: String(lng),
        status: 'active',
      });
      toast.success('Location created');
      onCreated({
        _id: res.data._id,
        name: res.data.name,
        code: res.data.code,
        state: lState.trim() || undefined,
        city: lCity.trim() || undefined,
        lat,
        lng,
      });
      reset();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not create location');
    } finally {
      setSaving(false);
    }
  };

  const pinned = lat !== null && lng !== null;

  return (
    <SovereignModal
      open={open}
      onClose={handleClose}
      title="Add new site location"
      subtitle="Pin the site on the map — coordinates are required"
      icon={MapPin}
      maxWidthClass="max-w-3xl"
      footer={
        <>
          <button type="button" onClick={handleClose} className="rounded-lg px-4 py-2 text-sm font-semibold transition hover:opacity-90" style={svSecondaryBtn}>
            Cancel
          </button>
          <button
            type="submit"
            form="add-location-form"
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
            style={PRIMARY_BTN}
          >
            {saving ? 'Saving…' : (<><Plus className="h-4 w-4" strokeWidth={2.5} />Save location</>)}
          </button>
        </>
      }
    >
      <form id="add-location-form" onSubmit={(e) => void handleSave(e)} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel hint="Required">Location name</FieldLabel>
            <input value={lName} onChange={(e) => setLName(e.target.value)} required className={svInput} placeholder="e.g. Rajasthan Site A" />
          </div>
          <div>
            <FieldLabel hint="Required">Location code</FieldLabel>
            <input value={lCode} onChange={(e) => setLCode(e.target.value.toUpperCase())} required className={svInput} placeholder="e.g. RAJ-A" maxLength={30} />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <FieldLabel hint="Required">Location type</FieldLabel>
            <select value={lType} onChange={(e) => setLType(e.target.value as CreateLocationData['type'])} className={svInput}>
              {(['Project Site', 'Office', 'Warehouse', 'Service Center', 'Other'] as const).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <FieldLabel hint="Optional">State</FieldLabel>
            <input value={lState} onChange={(e) => setLState(e.target.value)} className={svInput} placeholder="e.g. Rajasthan" />
          </div>
          <div>
            <FieldLabel hint="Optional">City / District</FieldLabel>
            <input value={lCity} onChange={(e) => setLCity(e.target.value)} className={svInput} placeholder="e.g. Jodhpur" />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel hint="Required">Latitude</FieldLabel>
            <input inputMode="decimal" value={lLat} onChange={(e) => setLLat(e.target.value)} required className={svInput} placeholder="e.g. 26.2389" />
            {latWarn ? <HelperText tone="warn">{latWarn}</HelperText> : null}
          </div>
          <div>
            <FieldLabel hint="Required">Longitude</FieldLabel>
            <input inputMode="decimal" value={lLng} onChange={(e) => setLLng(e.target.value)} required className={svInput} placeholder="e.g. 73.0243" />
            {lngWarn ? <HelperText tone="warn">{lngWarn}</HelperText> : null}
          </div>
        </div>

        <div>
          <FieldLabel hint="Click to drop the pin">Pin on map</FieldLabel>
          <LocationMiniMap
            height={240}
            onPick={handlePick}
            points={pinned ? [{ id: 'new-site', name: lName.trim() || 'New site', lat: lat as number, lng: lng as number, primary: true }] : []}
          />
          <HelperText tone={pinned ? 'info' : 'muted'}>
            {pinned ? `Pinned at ${lat}, ${lng}` : 'Click anywhere on the map to fill latitude & longitude automatically.'}
          </HelperText>
        </div>
      </form>
    </SovereignModal>
  );
}
